import { FunctionComponent } from 'react';

import {
  serializationOptionsForBooleanValue,
  serializationOptionsForNumberArrayValue,
  serializationOptionsForNumberValue,
  serializationOptionsForStringArrayValue,
  serializationOptionsForStringValue,
  useSearchParamState,
} from 'utils/hooks/useSearchParamState';
import { stripPrefix } from 'utils/stringHelpers';

import {
  FILTER_PREFIXES,
  PARENT_CAMPAIGN_ID_SEARCH_PARAM,
  QUERY_PARAMS,
  UNSET_PARENT_CAMPAIGN_ID,
} from '../../constants';
import { StateCategory } from '../../types';
import { CampaignFiltersContext, CampaignFiltersState } from './CampaignFiltersContext';

type SearchParamStateSetter = (value: string[]) => void;

const stripPrefixesFor =
  (setterFn: SearchParamStateSetter) => (prefix: string) => (values: string[]) =>
    setterFn(values.map((value) => stripPrefix(prefix, value)));

export const CampaignFiltersContextProvider: FunctionComponent<
  React.PropsWithChildren<unknown>
> = ({ children }) => {
  const [searchQuery, setSearchQuery] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.SEARCH,
    stateValueWhenSearchParamIsUnset: '',
    pushChangesToBrowserHistory: true,
    ...serializationOptionsForStringValue,
  });

  const [media, setMedia] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.MEDIA,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });

  const [creators, setCreators] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.CREATORS,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });
  const [modifiers, setModifiers] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.MODIFIERS,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });

  const [types, setTypes] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.TYPES,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });
  const [stateCategory, setStateCategory] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.STATE_CATEGORY,
    stateValueWhenSearchParamIsUnset: '',
    ...serializationOptionsForStringValue,
  });
  const [states, setStates] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.STATES,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });
  const [labels, setLabels] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.LABELS,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForNumberArrayValue,
  });
  const [lists, setLists] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.LISTS,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForNumberArrayValue,
  });
  const [channelTypes, setChannelTypes] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.CHANNELS,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });

  const [parentCampaignId, setParentCampaignId] = useSearchParamState({
    searchParamKey: PARENT_CAMPAIGN_ID_SEARCH_PARAM,
    stateValueWhenSearchParamIsUnset: UNSET_PARENT_CAMPAIGN_ID,
    ...serializationOptionsForNumberValue,
  });

  const [placements, setPlacements] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.PLACEMENTS,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForNumberArrayValue,
  });

  const [alerts, setAlerts] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.ALERTS,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });

  const [templateType, setTemplateType] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.TEMPLATE_TYPES,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });

  const [templateStatus, setTemplateStatus] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.TEMPLATE_STATUSES,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });

  const [templateQualityRating, setTemplateQualityRating] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.TEMPLATE_QUALITY_RATINGS,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });

  const [isFavorites, setIsFavorites] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.IS_FAVORITES,
    stateValueWhenSearchParamIsUnset: false,
    ...serializationOptionsForBooleanValue,
  });

  const [hideFolders, setHideFolders] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.HIDE_FOLDERS,
    stateValueWhenSearchParamIsUnset: false,
    ...serializationOptionsForBooleanValue,
  });

  const clearFilters = () => {
    setMedia([]);
    setCreators([]);
    setModifiers([]);
    setTypes([]);
    setStates([]);
    setLabels([]);
    setLists([]);
    setChannelTypes([]);
    setPlacements([]);
    setAlerts([]);
    setTemplateType([]);
    setTemplateStatus([]);
    setTemplateQualityRating([]);
  };

  const context: CampaignFiltersState = {
    clearFilters,
    campaignTypes: types,
    setCampaignTypes: stripPrefixesFor(setTypes)(FILTER_PREFIXES.CAMPAIGN_TYPE),
    channelTypes,
    setChannelTypes: stripPrefixesFor(setChannelTypes)(FILTER_PREFIXES.CHANNEL_TYPE),
    creators,
    setCreators: stripPrefixesFor(setCreators)(FILTER_PREFIXES.CREATOR),
    labels,
    setLabels,
    lists,
    setLists,
    media,
    setMedia: stripPrefixesFor(setMedia)(FILTER_PREFIXES.MEDIUM),
    modifiers,
    setModifiers: stripPrefixesFor(setModifiers)(FILTER_PREFIXES.MODIFIER),
    parentCampaignId: parentCampaignId === UNSET_PARENT_CAMPAIGN_ID ? undefined : parentCampaignId,
    setParentCampaignId: (value?: number) => setParentCampaignId(value ?? UNSET_PARENT_CAMPAIGN_ID),
    placements,
    setPlacements,
    stateCategory: stateCategory as StateCategory,
    setStateCategory,
    states,
    setStates: stripPrefixesFor(setStates)(FILTER_PREFIXES.STATE),
    searchQuery,
    setSearchQuery,
    alerts,
    setAlerts,
    templateType,
    setTemplateType,
    templateStatus,
    setTemplateStatus,
    templateQualityRating,
    setTemplateQualityRating,
    isFavorites,
    setIsFavorites,
    hideFolders,
    setHideFolders,
  };

  return (
    <CampaignFiltersContext.Provider value={context}>{children}</CampaignFiltersContext.Provider>
  );
};
